import { Drawer } from '@material-tailwind/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChosenVariantDto } from '../../../constants/types';
import Plans from '../../../components/options/plans';
import { PageContainer } from '../../../components/layout/page/PageContainer';
import TextWinds from '../../../components/common/TextWinds';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import {
  getCourseInfo,
  selectCourseInfo,
} from '../../../store/features/courseSlice';
import { Options } from '../../../components/options/options';
import { getOrderItem } from '../../../api/utils/common-purchase';
import { ChosenOptionsDto } from '../../../store/features/shopSlice';
import { addCart } from '../../../store/features/cartSlice';
import { excuteAxiosPost } from '../../../api/axios/call/testAxiosCall';
import useUserHook from '../../../hooks/useUserHook';
import LeftContents from '../../../components/course/LeftContents';
import instance from '../../../api/axios/utils.ts/instance';
import { CourseType } from '../../../enum';
import { shouldCheckCourseUsage } from '../../../utils/product-usage-check';
import HomeschoolMain from '../../../components/course/homeschool/HomeschoolMain';
import WmwMain from '../../../components/course/churchschool/wmw/WmwMain';
import HFHJMain from '../../../components/course/churchschool/hfhj/HFHJMain';
import ProductDetailContents from '../../../components/common/ProductDetailContents';
import { openAlert, openConfirm } from '../../../store/features/modalSlice';
import {
  isProductPurchasable,
  PRODUCT_INACTIVE_MESSAGE,
} from '../../../utils/product-active';

const CourseDetails = () => {
  const id = useParams().homeschoolId;
  const [searchParams, setSearchParams] = useSearchParams();
  const path = '/v1/product/' + id;
  const dispatch = useAppDispatch();
  const courseInfo = useAppSelector(selectCourseInfo);
  const { checkWithCallback } = useUserHook();

  useEffect(() => {
    dispatch(getCourseInfo({ url: path }));
  }, [path]);

  const [hasCourse, setHasCourse] = useState<boolean>(false);
  useEffect(() => {
    const fetchCourseUsage = async () => {
      if (shouldCheckCourseUsage(id)) {
        const params = {
          courseId:
            courseInfo.courses.filter(
              (a: any) =>
                CourseType.TEACHING === a.course.type ||
                CourseType.TRAINING === a.course.type,
            ).length > 0
              ? courseInfo.courses
                  .filter((a: any) => CourseType.TRAINING === a.course.type)
                  .map((x: any) => x.course.id)
              : courseInfo.courses
                  .filter((a: any) => CourseType.LEARNING === a.course.type)
                  .map((x: any) => x.course.id),
        };
        try {
          await instance
            .get(`/v1/product/course/usage`, { params: params })
            .then((res) => {
              if (200 === res.status) {
                const result = res.data.data;
                setHasCourse(result.hasCourse);
              }
            });
        } catch (error) {}
      } else {
        setHasCourse(false);
      }
    };
    if (id === courseInfo?.id) {
      // 로그인 상태에서만 조회
      const token = localStorage.getItem('accessToken');

      if (token) {
        fetchCourseUsage();
      }
    }
  }, [courseInfo]);

  // 팝업 공통 기능
  const [openBottom, setOpenBottom] = useState(
    !!searchParams.get('optionItems') || !!searchParams.get('palnItems'),
  );
  const openDrawerBottom = () => {
    setActiveStep(0);
    searchParams.delete('optionItems');
    setSearchParams(searchParams);
    setOpenBottom(true);
  };
  const redirectToJebsphoneLink = () => {
    const url = 'https://www.jebsphone.com/';
    window.open(url, '_blank', 'noopener, noreferrer');
  };
  const closeDrawerBottom = () => {
    setOpenBottom(false);
  };

  // PLAN
  const [activeStep, setActiveStep] = useState(
    !!searchParams.get('optionItems') === true ? 1 : 0,
  );

  const isLoading = (): boolean => {
    if (!courseInfo) return true;
    return courseInfo.id != id;
  };

  /** 판매 중지(is_active === false) 상품이면 구매 불가 */
  const isPurchasable = isProductPurchasable(courseInfo);

  // OPTION
  // 1. 구매하기
  const navigate = useNavigate();
  const onPurchaseHandle = async (
    _childId: string,
    selectOptionData: ChosenVariantDto[],
  ) => {
    if (!isPurchasable) {
      dispatch(openAlert({ message: PRODUCT_INACTIVE_MESSAGE }));
      return;
    }
    const action = async () => {
      const path = '/v1/order/template/from-prod';
      const params = {
        order_item: getOrderItem({
          product_id: courseInfo.id,
          variants: [],
          // 옵션 상품
          options: selectOptionData.reduce<ChosenOptionsDto[]>((acc, cur) => {
            acc.push({
              optionProduct: courseInfo.option_products[0],
              chosenVariant: cur.variant,
              quantity: cur.quantity,
            });
            return acc;
          }, []),
          enrolls: [courseInfo.enrolls[0]],
        }),
      };
      await Promise.all([excuteAxiosPost(path, params)]).then((res) => {
        if (res) {
          navigate(
            { pathname: '/purchase' },
            { state: { purchaseId: res[0].data.id } },
          );
        }
      });
    };
    return await checkWithCallback(action);
  };
  // 2. 장바구니
  const onCartHandle = useCallback(
    async (childId: string, selectItem: ChosenVariantDto[]) => {
      if (!isPurchasable) {
        dispatch(openAlert({ message: PRODUCT_INACTIVE_MESSAGE }));
        return;
      }
      if (selectItem.length > 0) {
        try {
          const res = await dispatch(
            addCart({
              url: '/v1/cart',
              data: {
                order_item: getOrderItem({
                  product_id: courseInfo.id,
                  // 안넣으면 오류나서 추가함.
                  variants: [
                    {
                      variant: selectItem[0].variant,
                      baseQuantity: 1,
                      quantity: 1,
                      totalPrice: courseInfo.current_price,
                    },
                  ],
                  // 옵션 상품
                  options: selectItem.reduce<ChosenOptionsDto[]>((acc, cur) => {
                    acc.push({
                      optionProduct: courseInfo.option_products[0],
                      chosenVariant:
                        courseInfo.option_products[0].option_product
                          .option_variants[0],
                      quantity: cur.quantity,
                    });
                    return acc;
                  }, []),
                  enrolls: [],
                }),
              },
            }),
          ).unwrap();

          if (res) {
            dispatch(
              openConfirm({
                message: '장바구니에 추가되었습니다.',
                onConfirm: () => {
                  navigate('/cart');
                },
                confirmText: '장바구니 보러 가기',
                cancelText: '상품 계속 담기',
              }),
            );
          }
        } catch (e) {
          dispatch(
            openAlert({
              message:
                '일시적인 장애로 상품을 장바구니에 담을 수 없습니다. 나중에 다시 시도해주세요.',
            }),
          );
        }
      }
    },
    [courseInfo, isPurchasable],
  );

  /**
   * 2025.12 설명회 '홈스쿨' -> '영어교실'로 변경되면서, 홈스쿨은 name으로 비교 불가하여 주석처리함
   */
  const COURSE_COMPONENT_MAP = {
    // 홈스쿨: HomeschoolMain,
    'Who Made the World': WmwMain,
    'Hello, Father! Hello, Jesus!': HFHJMain,
  };

  /**
   * 코스 상세 페이지 컴포넌트 매핑
   * 해당 코스의 상세페이지는 신어드민에서 등록한 코스 상세 이미지가 아닌, 컴포넌트로 매핑함
   */
  const courseStaticDetailPage = () => {
    // HOMESCHOOL은 product_sub_type과 비교
    if (courseInfo.product_sub_type === 'HOMESCHOOL') {
      return React.createElement(HomeschoolMain);
    }

    // 나머지는 name과 비교
    const matchedComponent = Object.entries(COURSE_COMPONENT_MAP).find(
      ([key]) => key !== 'HOMESCHOOL' && courseInfo.name.includes(key),
    );
    return matchedComponent ? React.createElement(matchedComponent[1]) : false;
  };

  return isLoading() ? null : (
    <PageContainer>
      <div className="flex flex-col gap-y-6 pt-10">
        <TextWinds type="title_h2" xlType="title_h1" className="px-4">
          {/* 홈스쿨 어드벤스드 코드 */}
          {courseInfo.name}
        </TextWinds>

        <div className="flex flex-col gap-[32px] lg:flex-row lg:pt-[24px]">
          <div className="flex w-full shrink-0 lg:w-[275px]">
            {courseInfo && (
              <LeftContents
                hasCourse={hasCourse}
                courseInfo={courseInfo}
                redirectToJebsphoneLink={redirectToJebsphoneLink}
                openDrawerBottom={openDrawerBottom}
              />
            )}
          </div>
          <div className="flex basis-full flex-col">
            {courseStaticDetailPage() ||
              (courseInfo && courseInfo.contents && (
                <ProductDetailContents
                  contents={courseInfo.contents}
                  productName={courseInfo.name}
                  productSubType={courseInfo.product_sub_type}
                />
              ))}
          </div>
          <div
            className={`fixed bottom-0 left-0 w-full p-4 ${
              openBottom ? 'block' : 'hidden'
            }`}
          >
            <Drawer
              placement="bottom"
              open={openBottom}
              onClose={closeDrawerBottom}
              overlay={false}
              size={500}
              className="relative right-1/4 m-auto w-full items-center rounded-2xl px-5 py-4 drop-shadow-xl lg:w-1/2"
            >
              {activeStep === 0 ? (
                <Plans
                  hasCourse={hasCourse}
                  closeDrawerBottom={closeDrawerBottom}
                  handleNext={() => setActiveStep((cur) => cur + 1)}
                  handleSubmit={onPurchaseHandle}
                  data={courseInfo}
                />
              ) : (
                <Options
                  gubun="course"
                  shopInfo={courseInfo}
                  chosenVariants={[
                    ...courseInfo.option_products[0].option_product
                      .option_variants,
                  ].sort((a, b) => a.name.localeCompare(b.name))}
                  closeDrawerBottom={closeDrawerBottom}
                  onCartHandle={onCartHandle}
                  onPurchaseHandle={onPurchaseHandle}
                  isPointProduct={false}
                />
              )}
            </Drawer>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CourseDetails;
